import { API_BASE } from '../apiBase';
// src/billing/QuickCreateBid.js
// Inline card for the quote screen: create a customer (if new) + a BID —
// NOT a project. No PO is assigned, no Dropbox folders are created. The
// project is born later, when the bid is won and promoted.
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAddressSuggestions, geocodePlaceId } from '../lib/googlePlaces';

// live-format a phone as xxx-xxx-xxxx while typing
const fmtPhone = (v) => {
  const d = String(v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

export default function QuickCreateBid({ seedTitle = '', onDone, onCancel }) {
  const [form, setForm] = useState({ title: seedTitle, customerId: '', customerName: '', custSearch: '', street: '', city: '', state: 'CA', zip: '' });
  const [customers, setCustomers] = useState([]);
  const [showCustList, setShowCustList] = useState(false);
  const [newCust, setNewCust] = useState(null); // { firstName, lastName, phone, email } | null
  // customer billing address: defaults to the job site; uncheck to enter a different one
  const [billSame, setBillSame] = useState(true);
  const [bill, setBill] = useState({ street: '', city: '', state: 'CA', zip: '' });
  const [billText, setBillText] = useState('');
  const [billSugs, setBillSugs] = useState([]);
  const [billErr, setBillErr] = useState(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billManual, setBillManual] = useState(false);
  const billTimer = useRef(null);
  const handleBillSearch = (val) => {
    setBillText(val);
    setBill(prev => ({ ...prev, street: val, city: '', zip: '' }));
    clearTimeout(billTimer.current);
    if (val.trim().length < 3) { setBillSugs([]); setBillOpen(false); return; }
    billTimer.current = setTimeout(async () => {
      const { predictions, error: err } = await getAddressSuggestions(val);
      setBillErr(err); setBillSugs(predictions); setBillOpen(true);
    }, 350);
  };
  const pickBill = async (p) => {
    setBillOpen(false); setBillText(p.description);
    try {
      const parts = await geocodePlaceId(p.placeId);
      setBill(parts);
      setBillText([parts.street, parts.city, `${parts.state} ${parts.zip}`.trim()].filter(Boolean).join(', '));
    } catch (e) { setBill(prev => ({ ...prev, street: p.description })); }
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [addrSugs, setAddrSugs] = useState([]);
  const [addrErr, setAddrErr] = useState(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [manualAddr, setManualAddr] = useState(false);
  const [addrText, setAddrText] = useState(''); // what the search field displays (full address once picked)
  const addrTimer = useRef(null);
  const fullAddr = (f) => [f.street, f.city, `${f.state || ''} ${f.zip || ''}`.trim()].filter(Boolean).join(', ');

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/customers`, { headers: { Authorization: `Bearer ${token}` } });
        setCustomers(res.data || []);
      } catch (e) { /* user can still quick-create a customer */ }
    })();
  }, []);

  const handleStreet = (val) => {
    setAddrText(val);
    setForm(prev => ({ ...prev, street: val, city: '', zip: '' })); // typing = starting over
    clearTimeout(addrTimer.current);
    if (val.trim().length < 3) { setAddrSugs([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      const { predictions, error: err } = await getAddressSuggestions(val);
      setAddrErr(err); setAddrSugs(predictions); setAddrOpen(true);
    }, 350);
  };
  const pickAddress = async (p) => {
    setAddrOpen(false);
    setAddrText(p.description);
    try {
      const parts = await geocodePlaceId(p.placeId);
      setForm(prev => ({ ...prev, street: parts.street, city: parts.city, state: parts.state, zip: parts.zip }));
      setAddrText(fullAddr(parts)); // field shows the whole thing: street, city, state zip
    } catch (e) {
      setForm(prev => ({ ...prev, street: p.description }));
    }
  };

  const save = async () => {
    if (!form.title.trim()) { setError('Job title is required'); return; }
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('token');
      let customerId = form.customerId;
      if (!customerId) {
        if (!newCust?.firstName?.trim()) { setError('Pick a customer or enter a first name for a new one'); setSaving(false); return; }
        const custPayload = {
          firstName: newCust.firstName.trim(), lastName: (newCust.lastName || '').trim(),
          name: `${newCust.firstName} ${newCust.lastName || ''}`.replace(/\s+/g, ' ').trim(),
          type: 'Homeowner', status: 'Lead',
          primaryPhone: (newCust.phone || '').trim(), primaryEmail: (newCust.email || '').trim(),
          // billing address prints in the quote's "QUOTE TO:" — job site if same,
          // otherwise whatever was entered above
          billingAddress: billSame
            ? { street: form.street, city: form.city, state: form.state || 'CA', zip: form.zip }
            : { street: bill.street, city: bill.city, state: bill.state || 'CA', zip: bill.zip },
        };
        const cRes = await axios.post(`${API_BASE}/customers`, custPayload, { headers: { Authorization: `Bearer ${token}` } });
        customerId = cRes.data._id;
      }
      const bRes = await axios.post(`${API_BASE}/bids`, {
        title: form.title.trim(),
        customer: customerId,
        stage: 'Bidding',
        jobSiteAddress: { street: form.street, city: form.city, state: form.state || 'CA', zip: form.zip, country: 'USA' },
      }, { headers: { Authorization: token } });
      onDone(bRes.data); // populated bid
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create bid');
    } finally {
      setSaving(false);
    }
  };

  const field = { padding: '10px 12px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '14px' };
  const linkBtn = { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '13px' };

  return (
    <div style={{ marginTop: '10px', border: '1px solid #c7d2fe', background: '#f8faff', borderRadius: '8px', padding: '16px' }}>
      <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '.05em' }}>New bid</p>
      <input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Job title * (e.g. Johnson panel upgrade)"
        style={{ ...field, width: '100%', marginBottom: '8px', boxSizing: 'border-box' }}
      />

      {form.customerId ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
          <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '4px 10px', borderRadius: '999px', fontWeight: 600 }}>{form.customerName}</span>
          <button type="button" onClick={() => setForm({ ...form, customerId: '', customerName: '' })} style={linkBtn}>change</button>
        </div>
      ) : newCust ? (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <input value={newCust.firstName} onChange={(e) => setNewCust({ ...newCust, firstName: e.target.value })} placeholder="First name *" style={field} />
            <input value={newCust.lastName} onChange={(e) => setNewCust({ ...newCust, lastName: e.target.value })} placeholder="Last name" style={field} />
            <input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: fmtPhone(e.target.value) })} placeholder="Phone (xxx-xxx-xxxx)" style={field} />
            <input value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} placeholder="Email" style={field} />
          </div>
          <button type="button" onClick={() => setNewCust(null)} style={{ ...linkBtn, padding: 0 }}>← back to existing customers</button>

          {/* customer BILLING address — often the job site, but not always */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0 0', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={billSame} onChange={(e) => setBillSame(e.target.checked)} style={{ width: '15px', height: '15px' }} />
            Billing address same as job site address
          </label>
          {!billSame && (
            billManual ? (
              <div style={{ marginTop: '8px' }}>
                <input value={bill.street} onChange={(e) => setBill({ ...bill, street: e.target.value })} placeholder="Billing street" style={{ ...field, width: '100%', boxSizing: 'border-box', marginBottom: '8px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '2fr .8fr 1fr', gap: '8px' }}>
                  <input value={bill.city} onChange={(e) => setBill({ ...bill, city: e.target.value })} placeholder="City" style={field} />
                  <input value={bill.state} onChange={(e) => setBill({ ...bill, state: e.target.value })} placeholder="State" style={field} />
                  <input value={bill.zip} onChange={(e) => setBill({ ...bill, zip: e.target.value })} placeholder="ZIP" style={field} />
                </div>
                <button type="button" onClick={() => setBillManual(false)} style={{ ...linkBtn, padding: '6px 0 0' }}>← use address search instead</button>
              </div>
            ) : (
              <div style={{ position: 'relative', marginTop: '8px' }}>
                <input value={billText} onChange={(e) => handleBillSearch(e.target.value)}
                  onFocus={() => { if (billSugs.length || billErr) setBillOpen(true); }}
                  onBlur={() => setTimeout(() => setBillOpen(false), 250)}
                  placeholder="Billing address — start typing to search…" style={{ ...field, width: '100%', boxSizing: 'border-box' }} />
                {billOpen && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', background: 'white', zIndex: 45, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
                    {billErr ? (
                      <div style={{ padding: '10px 12px', color: '#ef4632', fontSize: '13px' }}>{billErr}</div>
                    ) : billSugs.length === 0 ? (
                      <div style={{ padding: '10px 12px', color: '#888', fontSize: '13px' }}>No matching addresses</div>
                    ) : (
                      billSugs.map((p, i) => (
                        <div key={i} onClick={() => pickBill(p)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>{p.description}</div>
                      ))
                    )}
                    <div onMouseDown={(e) => { e.preventDefault(); setBillOpen(false); setBillManual(true); }}
                      style={{ padding: '10px 12px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, fontSize: '13px', borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}>
                      Enter address manually
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <input
            value={form.custSearch}
            onChange={(e) => { setForm({ ...form, custSearch: e.target.value }); setShowCustList(true); }}
            onFocus={() => setShowCustList(true)}
            onBlur={() => setTimeout(() => setShowCustList(false), 250)}
            placeholder="Customer * — type to search…"
            style={{ ...field, width: '100%', boxSizing: 'border-box' }}
          />
          {showCustList && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '180px', overflowY: 'auto', background: 'white', zIndex: 30, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
              {customers.filter(c => (c.name || '').toLowerCase().includes(form.custSearch.toLowerCase())).slice(0, 8).map(c => (
                <div key={c._id} onClick={() => { setForm({ ...form, customerId: c._id, customerName: c.name }); setShowCustList(false); }} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
                  {c.name} <span style={{ color: '#9ca3af', fontSize: '12px' }}>({c.type})</span>
                </div>
              ))}
              <div onMouseDown={(e) => { e.preventDefault(); const parts = form.custSearch.trim().split(/\s+/); setNewCust({ firstName: parts[0] || '', lastName: parts.slice(1).join(' '), phone: '', email: '' }); setShowCustList(false); }}
                style={{ padding: '10px 12px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, fontSize: '14px', position: 'sticky', bottom: 0, background: 'white', borderTop: '1px solid #f0f0f0' }}>
                + New customer{form.custSearch.trim() ? ` “${form.custSearch.trim()}”` : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {manualAddr ? (
        <div style={{ marginBottom: '8px' }}>
          <input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="Street" style={{ ...field, width: '100%', boxSizing: 'border-box', marginBottom: '8px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr .8fr 1fr', gap: '8px' }}>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" style={field} />
            <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" style={field} />
            <input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="ZIP" style={field} />
          </div>
          <button type="button" onClick={() => { setAddrText(fullAddr(form)); setManualAddr(false); }} style={{ ...linkBtn, padding: '6px 0 0' }}>← use address search instead</button>
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <input value={addrText} onChange={(e) => handleStreet(e.target.value)}
            onFocus={() => { if (addrSugs.length || addrErr) setAddrOpen(true); }}
            onBlur={() => setTimeout(() => setAddrOpen(false), 250)}
            placeholder="Job address (optional) — start typing to search…" style={{ ...field, width: '100%', boxSizing: 'border-box' }} />
          {addrOpen && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '190px', overflowY: 'auto', background: 'white', zIndex: 40, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
              {addrErr ? (
                <div style={{ padding: '10px 12px', color: '#ef4632', fontSize: '13px' }}>{addrErr}</div>
              ) : addrSugs.length === 0 ? (
                <div style={{ padding: '10px 12px', color: '#888', fontSize: '13px' }}>No matching addresses</div>
              ) : (
                addrSugs.map((p, i) => (
                  <div key={i} onClick={() => pickAddress(p)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
                    {p.description}
                  </div>
                ))
              )}
              <div onMouseDown={(e) => { e.preventDefault(); setAddrOpen(false); setManualAddr(true); }}
                style={{ padding: '10px 12px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, fontSize: '13px', borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}>
                Enter address manually
              </div>
            </div>
          )}
          <button type="button" onClick={() => setManualAddr(true)} style={{ ...linkBtn, padding: '6px 0 0' }}>enter manually</button>
        </div>
      )}

      {error && <div style={{ color: '#ef4632', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" onClick={onCancel} style={{ flex: 1, padding: '10px', background: '#eef0f3', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button type="button" onClick={save} disabled={saving} style={{ flex: 1, padding: '10px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Creating…' : 'Create bid & quote it'}
        </button>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>No PO, no folders — those happen when the bid is won. This just creates the customer (if new) and the opportunity.</p>
    </div>
  );
}
